import { onBeforeUnmount, onMounted, ref, type Ref } from 'vue'

/**
 * Pointer-occupancy detection for an element's "zone of influence".
 *
 * Reports whether the pointer currently sits inside a rectangle derived from the
 * target element's bounding box, optionally grown on each edge (and optionally
 * spanning the full viewport width). Unlike a real invisible hit-area, this
 * captures *nothing* — it only reads the pointer position — so content beneath
 * the zone stays fully interactive.
 *
 * Intended use is a "comes alive on approach" affordance: drive a CSS class from
 * `near` so a surface can warm up as the cursor enters its region, slightly
 * before contact. Pair it with the element's own `:hover` / `:focus-within` so
 * behavior degrades gracefully if this never engages (keyboard, no-JS, touch).
 *
 * Gated to fine, hover-capable pointers: on touch there is no hover state and
 * `pointermove` only fires mid-touch, so `near` simply stays `false` there.
 */
const FINE_HOVER_QUERY = '(hover: hover) and (pointer: fine)'

export interface ProximityOptions {
  /** px to extend the active zone above the element's top edge. */
  top?: number
  /** px to extend beyond the right edge (ignored when `fullWidth`). */
  right?: number
  /** px to extend below the bottom edge. */
  bottom?: number
  /** px to extend beyond the left edge (ignored when `fullWidth`). */
  left?: number
  /** When true the zone spans the whole viewport width; `left`/`right` ignored. */
  fullWidth?: boolean
}

export function useProximity(
  targetRef: Ref<HTMLElement | null>,
  options: ProximityOptions = {},
) {
  const { top = 0, right = 0, bottom = 0, left = 0, fullWidth = false } = options

  const near = ref(false)

  const hasWindow = typeof window !== 'undefined'
  let enabled = false
  let ticking = false
  let lastX = 0
  let lastY = 0

  function evaluate() {
    ticking = false

    const el = targetRef.value
    if (!el) {
      near.value = false
      return
    }

    // Pointer client coords and getBoundingClientRect are both viewport-relative,
    // so they compare directly (the chrome being position:fixed makes this exact).
    const rect = el.getBoundingClientRect()

    const withinY = lastY >= rect.top - top && lastY <= rect.bottom + bottom
    const withinX = fullWidth
      ? true
      : lastX >= rect.left - left && lastX <= rect.right + right

    near.value = withinX && withinY
  }

  function onPointerMove(e: PointerEvent) {
    lastX = e.clientX
    lastY = e.clientY
    // Coalesce a flood of move events into at most one evaluation per frame.
    if (ticking) return
    ticking = true
    requestAnimationFrame(evaluate)
  }

  // The pointer leaving the document fires no further moves, so reset explicitly
  // to avoid `near` sticking true when the cursor exits through the zone's edge.
  function onPointerLeave() {
    near.value = false
  }

  onMounted(() => {
    if (!hasWindow || !window.matchMedia) return
    if (!window.matchMedia(FINE_HOVER_QUERY).matches) return

    enabled = true
    window.addEventListener('pointermove', onPointerMove, { passive: true })
    document.documentElement.addEventListener('pointerleave', onPointerLeave)
  })

  onBeforeUnmount(() => {
    if (!enabled) return
    window.removeEventListener('pointermove', onPointerMove)
    document.documentElement.removeEventListener('pointerleave', onPointerLeave)
    ticking = false
  })

  return { near }
}
